import {
  CUSTOMER_TYPE_LABELS,
  CUSTOMER_TYPES,
  masters,
  type CustomerType,
} from "@nexseed/shared";
import { Store } from "lucide-react";
import { defineMaster, type MasterRow } from "../spec";

/** Text of a raw SQL value: strings as they are, numbers and dates spelt out, null as "". */
const txt = (v: unknown): string =>
  typeof v === "string"
    ? v
    : typeof v === "number" || typeof v === "bigint" || typeof v === "boolean"
      ? String(v)
      : v instanceof Date
        ? v.toISOString()
        : v === null || v === undefined
          ? ""
          : JSON.stringify(v);

/**
 * Customer Master (Orders and Dispatch, user 2026-10-30): the parties orders are booked for and
 * dispatched to. Loaded from SeedTrac's order and dispatch notes; SeedTrac's own "Name-Location" is
 * kept as Legacy name so its history can be matched back.
 */
const typeLabel = (t: unknown) =>
  CUSTOMER_TYPE_LABELS[t as CustomerType] ?? "—";
const TYPE_OPTIONS = CUSTOMER_TYPES.map((t) => ({
  value: t,
  label: CUSTOMER_TYPE_LABELS[t],
}));

export const customers = defineMaster({
  key: "customers",
  title: "Customer Master",
  singular: "Customer",
  description:
    "The parties orders are booked for and dispatched to: our branches and C&F depots, dealers, bulk and export buyers, TDF individuals.",
  icon: Store,
  schema: masters.customer.create,
  searchPlaceholder: "Search name, location or GSTIN",
  defaultSort: "name",
  listFilter: {
    param: "customerType",
    label: "Type",
    allLabel: "All types",
    options: TYPE_OPTIONS,
  },
  optionLabel: (r: MasterRow) =>
    r.location ? `${String(r.name)} · ${txt(r.location)}` : String(r.name),
  columns: [
    { id: "name", header: "Name", sortable: true },
    {
      id: "customerType",
      header: "Type",
      sortable: true,
      cell: (r: MasterRow) => typeLabel(r.customerType),
    },
    { id: "location", header: "Location", sortable: true },
    { id: "state", header: "State", sortable: true },
    { id: "gstin", header: "GSTIN", kind: "mono" },
    { id: "isActive", header: "Status", kind: "status" },
  ],
  fields: [
    {
      name: "customerType",
      label: "Type",
      type: "enum",
      required: true,
      options: TYPE_OPTIONS,
    },
    { name: "name", label: "Name", type: "text", required: true },
    { name: "location", label: "Location", type: "text" },
    { name: "contactPerson", label: "Contact person", type: "text" },
    { name: "address", label: "Address", type: "textarea", colSpan: 2 },
    { name: "city", label: "City / Town", type: "text" },
    { name: "state", label: "State", type: "text" },
    { name: "pincode", label: "PIN code", type: "text" },
    { name: "country", label: "Country", type: "text" },
    { name: "gstin", label: "GSTIN", type: "text", transform: "upper" },
    { name: "pan", label: "PAN", type: "text", transform: "upper" },
    { name: "phone", label: "Phone", type: "text" },
    { name: "mobile", label: "Mobile", type: "text" },
    {
      name: "legacyName",
      label: "SeedTrac name",
      type: "text",
      colSpan: 2,
      description: "The party's name in SeedTrac (Name-Location).",
    },
    { name: "isActive", label: "Active", type: "switch" },
  ],
});
